import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BaseModal from "../../modals/BaseModal";
import { ImageUploader } from "../../shared/ImageUploader";
import { Langs } from "../../../domain/enums/common/LangsEnum";
import { Tabs, Tab } from "@mui/material";
import { DEFAULT_LANGUAGE } from "../../../Config";
import { Owner } from "../../../domain/enums/owner-enum";
import { Purpose } from "../../../domain/enums/purpose-enum";
import { useFileEncoder } from "../../../custom-hooks/useFileEncoder";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { IPartners, PartnersFormValues } from "../../../domain/models/Partners";
import { partnersFields, partnersSchemaFactory } from "../../../validation-schemas/PartnersSchemas";
import CustomToggle from "../../../custom-components/form-inputs/CustomToggle";
import TranslationFieldsRenderer from "../../shared/form-inputs/TranslationFieldsRenderer";
import { isBoolean } from "lodash";
import CustomInput from "../../../custom-components/form-inputs/CustomInput";

interface PartnersEditorProps {
    entity?: IPartners | null;
    onClose: () => void;
    onSave: (saved: IPartners) => void;
    translateFields: (
        translationFields: Record<string, string>,
        sourceLang: Langs,
        targetLang: Langs
    ) => Promise<Record<string, string>>;
}

const PartnersEditor: FC<PartnersEditorProps> = ({ entity, onClose, onSave, translateFields }) => {
    const { t } = useTranslation();
    const { encodeFiles } = useFileEncoder();
    const [selectedLang, setSelectedLang] = useState<Langs>(DEFAULT_LANGUAGE);
    const [existingImageUrl, setExistingImageUrl] = useState(entity?.pictureExists(Purpose.LOGO) ? entity.getPictureUrl() : null);
    const tabs = useMemo(() => Object.values(Langs), []);

    const translationsInitial = useMemo(() => {
        return Object.values(Langs).map((lang) => {
            const existing = entity?.translations?.find((t) => t?.languageCode === lang);
            return {
                languageCode: lang,
                partnerId: entity?.id,
                title: existing?.title ?? "",
                description: existing?.description ?? "",
            };
        }) as PartnersFormValues["translations"];
    }, [entity?.translations]);

    const { form } = useCustomFormik<PartnersFormValues>({
        initial: {
            files: entity?.files,
            websiteUrl: entity?.websiteUrl ?? "",
            translations: translationsInitial,
            isActive: isBoolean(entity?.isActive) ? entity?.isActive : true,
        } as PartnersFormValues,
        fields: partnersFields,
        schema: partnersSchemaFactory(t),
        onSubmit: (values) => {
            const validLangs = values.translations.filter(
                (l) => l.title?.trim()?.length && l.description?.trim()?.length
            );

            const updated: IPartners = {
                ...entity,
                ...values,
                translations: validLangs,
            };
            onSave(updated);
            onClose();
        },
    });

    const isDefaultLangValid = useMemo(() => {
        const defaultTr = form.values.translations.find((t) => t.languageCode === DEFAULT_LANGUAGE);
        return !!(defaultTr && defaultTr.title?.trim().length > 1 && defaultTr.description?.trim().length > 1);
    }, [form.values.translations]);

    const handleFileChange = useCallback(
        async (file: File | null) => {
            if (!file) {
                form.setFieldValue("files", []);
                return;
            }
            const dataUrl = await encodeFiles(file);
            const files = [
                {
                    order: 1,
                    ownerType: Owner.PARTNERS,
                    purposeType: Purpose.LOGO,
                    fileType: file.type.split("/")[1],
                    dataUrl,
                },
            ];
            form.setFieldValue("files", files);
        },
        [encodeFiles]
    );

    const handleTranslateEntity = async () => {
        const defaultTr = form.values.translations.find((t) => t.languageCode === DEFAULT_LANGUAGE);
        if (!defaultTr) return;

        const payload = {
            title: defaultTr.title ?? "",
            description: defaultTr.description ?? "",
        };

        try {
            const translated = await translateFields(payload, DEFAULT_LANGUAGE, selectedLang);
            const translatedTr = {
                languageCode: selectedLang,
                partnerId: entity?.id,
                title: translated.title ?? "",
                description: translated.description ?? "",
            } as PartnersFormValues["translations"][number];

            const idx = form.values.translations.findIndex((t) => t.languageCode === selectedLang);
            if (idx >= 0) {
                form.setFieldValue(`translations[${idx}]`, translatedTr);
            } else {
                form.setFieldValue("translations", [...form.values.translations, translatedTr]);
            }
        } catch (err) {
            console.error("translate error", err);
        }
    };

    return (
        <BaseModal
            isVisible={true}
            setIsVisible={onClose}
            title={entity ? t("console.partners.editPartner") : t("console.partners.createPartner")}
            confirmTitle={t("console.common.save")}
            cancelTitle={t("console.common.cancel")}
            confirmHandler={form.handleSubmit}
            closeHandler={onClose}
            additionalWrapClassName="w-[90vw]"
            confirmButtonDisabled={!form.isValid}
        >
            <div className="flex gap-6 h-[70vh]">
                <div className="flex flex-col gap-4 flex-[0.35]">
                    <div className="text-sm font-medium text-light-text-100 dark:text-dark-text-100">
                        {t("console.common.cover")}
                    </div>
                    <ImageUploader
                        imageUrl={existingImageUrl}
                        deleteImage={() => {
                            handleFileChange(null);
                            setExistingImageUrl(null);
                        }}
                        onChange={(file) => handleFileChange(file)}
                    />
                </div>

                <div className="flex flex-col flex-1 h-full overflow-auto">
                    <div className="flex flex-row justify-between">
                        <Tabs
                            value={selectedLang}
                            onChange={(_, newValue) => setSelectedLang(newValue as Langs)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {tabs.map((lang) => (
                                <Tab
                                    key={lang}
                                    value={lang}
                                    label={lang.toUpperCase()}
                                    sx={{ textTransform: "none", fontWeight: "medium" }}
                                    className="dark:text-dark-text-100"
                                />
                            ))}
                        </Tabs>

                        {selectedLang !== DEFAULT_LANGUAGE && (
                            <div className="flex items-center">
                                <button
                                    className={`w-48 text-white font-medium rounded-lg text-sm px-5 py-2 text-center ${isDefaultLangValid
                                        ? "bg-blue-700 hover:bg-blue-800"
                                        : "bg-gray-400 cursor-not-allowed"
                                        }`}
                                    onClick={handleTranslateEntity}
                                    disabled={!isDefaultLangValid}
                                    title={
                                        (!isDefaultLangValid &&
                                            `${t(
                                                "console.common.fillDefaultLangFirst"
                                            )} ${DEFAULT_LANGUAGE.toUpperCase()}`) ||
                                        ""
                                    }
                                >
                                    {t("console.common.translateFromDefault")}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="overflow-auto">
                        <p className="mt-1 ml-3 text-sm font-normal text-red-500">
                            {(typeof form.errors.translations === "string" && form.errors.translations) || "\u00A0"}
                        </p>

                        {form.values.translations.map((tr, index) => (
                            <div
                                key={tr.languageCode}
                                role="tabpanel"
                                hidden={selectedLang !== tr.languageCode}
                                className="overflow-auto"
                            >
                                <TranslationFieldsRenderer
                                    translation={tr}
                                    index={index}
                                    fieldConfigs={partnersFields.translations.objectArray}
                                    errors={(form.errors.translations?.[index] as any) || undefined}
                                    t={t}
                                    setFieldValue={(fieldName, value) => form.setFieldValue(fieldName, value)}
                                    handleFieldChange={(fieldName) => form.handleChange(fieldName)}
                                />
                            </div>
                        ))}
                        
                        <CustomInput
                            type="text"
                            labelKey={partnersFields.websiteUrl.placeholderKey}
                            name="websiteUrl"
                            placeholderKey={partnersFields.websiteUrl.placeholderKey}
                            value={form.values.websiteUrl}
                            onChange={form.handleChange("websiteUrl")}
                            errorKey={form.errors.websiteUrl}
                        />

                        <CustomToggle
                            labelKey={partnersFields.isActive.placeholderKey}
                            name={"isActive"}
                            value={form.values.isActive}
                            onChange={form.handleChange("isActive")}
                            errorKey={form.errors.isActive}
                        />
                    </div>
                </div>
            </div>
        </BaseModal>
    );
};

export default PartnersEditor;
